import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChatHistoryPage, ChatMessage, ReadReceipt, Uuid } from '../types/api';
import {
  getMyReceipt,
  listMessages,
  listThreadReceipts,
  markRead as apiMarkRead,
  sendMessage as apiSendMessage,
  type SendMessagePayload,
  type ThreadReceipts,
} from '../api/chat';
import { getSocket } from '../socket/socket';
import { WS_EVENTS } from '../socket/chat-events';
import { getTokenStore } from '../api/token-store';
import { refreshAccessToken } from '../api/client';

export function chatHistoryKey(transactionId: Uuid) {
  return ['chat', transactionId, 'history'] as const;
}
export function chatReceiptKey(transactionId: Uuid) {
  return ['chat', transactionId, 'receipt'] as const;
}
export function chatReceiptsKey(transactionId: Uuid) {
  return ['chat', transactionId, 'receipts'] as const;
}

/**
 * Tick state for a single message. Mirrors the WhatsApp convention:
 *   - SENT      → single gray tick (server hasn't confirmed delivery yet)
 *   - DELIVERED → double gray tick (server accepted + broadcast)
 *   - READ      → double blue tick (a counter-party has read past it)
 */
export type MessageDeliveryStatus = 'SENT' | 'DELIVERED' | 'READ';

/**
 * Pure helper. Given a message I sent, the read cursors of every party
 * in the thread, and my own id, derive the tick state for the message.
 * Returns null for messages I did not send — there are no ticks on the
 * other side's bubbles, only on mine.
 */
export function deriveMessageStatus(
  message: ChatMessage,
  peerReceipts: ReadReceipt[],
  myId: string | undefined,
): MessageDeliveryStatus | null {
  if (!myId || message.senderUserId !== myId) return null;
  if (!message.deliveredAt) return 'SENT';
  const createdMs = new Date(message.createdAt).getTime();
  // Read = any *other* party's lastReadAt has caught up to (or past)
  // this message's creation time. Admin counts as a counter-party for
  // tick purposes — if anyone on the thread has read it, blue.
  const anyPeerRead = peerReceipts.some((r) => {
    if (!r.userId || r.userId === myId) return false;
    if (!r.lastReadAt) return false;
    return new Date(r.lastReadAt).getTime() >= createdMs;
  });
  return anyPeerRead ? 'READ' : 'DELIVERED';
}

// Initial chat history (newest slice). Older pages can be fetched on demand
// via the returned `fetchOlder` callback — we manage that locally rather than
// using useInfiniteQuery to keep the WS merge logic simple.
export function useChatHistory(transactionId: Uuid | undefined) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: transactionId ? chatHistoryKey(transactionId) : ['chat', 'disabled'],
    queryFn: () => listMessages(transactionId as Uuid, { limit: 50 }),
    enabled: !!transactionId && !!getTokenStore().getAccessToken(),
    staleTime: 15_000,
  });

  async function fetchOlder() {
    if (!transactionId) return;
    const current = qc.getQueryData<ChatHistoryPage>(chatHistoryKey(transactionId));
    if (!current?.hasMore || !current.nextCursor) return;
    const older = await listMessages(transactionId, {
      beforeMessageId: current.nextCursor,
      limit: 50,
    });
    qc.setQueryData<ChatHistoryPage>(chatHistoryKey(transactionId), (prev) => ({
      items: [...(prev?.items ?? []), ...older.items],
      nextCursor: older.nextCursor,
      hasMore: older.hasMore,
    }));
  }

  return { ...query, fetchOlder };
}

export function useMyReceipt(transactionId: Uuid | undefined) {
  return useQuery({
    queryKey: transactionId ? chatReceiptKey(transactionId) : ['chat', 'receipt', 'disabled'],
    queryFn: () => getMyReceipt(transactionId as Uuid),
    enabled: !!transactionId && !!getTokenStore().getAccessToken(),
    staleTime: 30_000,
  });
}

// All parties' read cursors. The WS `READ_RECEIPT` listener in
// useChatSocket keeps this cache fresh as the counter-party reads.
export function useThreadReceipts(transactionId: Uuid | undefined) {
  return useQuery({
    queryKey: transactionId ? chatReceiptsKey(transactionId) : ['chat', 'receipts', 'disabled'],
    queryFn: () => listThreadReceipts(transactionId as Uuid),
    enabled: !!transactionId && !!getTokenStore().getAccessToken(),
    staleTime: 30_000,
  });
}

// Sends over the WS socket if connected, with REST fallback for reliability.
// Always call through useSendMessage so optimistic prepends stay consistent.
export function useSendMessage(transactionId: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SendMessagePayload): Promise<ChatMessage | void> => {
      const socket = getSocket();
      if (socket.connected) {
        return new Promise<ChatMessage>((resolve, reject) => {
          socket
            .timeout(5_000)
            .emit(
              WS_EVENTS.SEND,
              { transactionId, ...payload },
              (err: Error | null, ack?: { ok?: boolean; message?: ChatMessage }) => {
                if (err || !ack?.ok) reject(err ?? new Error('send failed'));
                else resolve(ack?.message!);
              },
            );
        }).catch(async () => {
          // WS path failed — fall back to REST. Server still fans out on WS.
          return apiSendMessage(transactionId, payload);
        });
      } else {
        return apiSendMessage(transactionId, payload);
      }
    },
    onSuccess: (newMsg) => {
      if (newMsg) {
        qc.setQueryData<ChatHistoryPage>(chatHistoryKey(transactionId), (prev) => {
          if (!prev) return { items: [newMsg], nextCursor: null, hasMore: false };
          if (prev.items.some((m) => m.id === newMsg.id)) return prev;
          return { ...prev, items: [newMsg, ...prev.items] };
        });
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: chatHistoryKey(transactionId) });
    },
  });
}

export function useMarkRead(transactionId: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lastReadMessageId: Uuid) => apiMarkRead(transactionId, lastReadMessageId),
    onMutate: () => {
      qc.setQueriesData({ queryKey: ['transactions'] }, (old: any) => {
        if (!old || !old.items) return old;
        return {
          ...old,
          items: old.items.map((t: any) =>
            t.id.toLowerCase() === transactionId.toLowerCase() ? { ...t, unreadCount: 0 } : t,
          ),
        };
      });
    },
    onSuccess: (receipt) => {
      qc.setQueryData(chatReceiptKey(transactionId), receipt);
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// Live socket binding for a single transaction thread. Joins on mount,
// subscribes to message_created / typing / read receipts, and merges inbound
// messages into the TanStack Query cache so React renders without a refetch.
export function useChatSocket(transactionId: Uuid | undefined) {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const [typingUserIds, setTypingUserIds] = useState<Set<string>>(() => new Set());
  const typingTimers = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!transactionId) return;
    const token = getTokenStore().getAccessToken();
    if (!token) return;

    const socket = getSocket();
    if (!socket.connected) socket.connect();

    function onConnect() {
      setConnected(true);
      socket.emit(WS_EVENTS.JOIN, { transactionId });
    }
    function onConnectError(err: any) {
      console.warn('Socket connection error, attempting token refresh:', err.message);
      refreshAccessToken()
        .then((newToken) => {
          if (newToken && !socket.connected) {
            socket.disconnect();
            socket.connect();
          }
        })
        .catch(() => {});
    }
    function onDisconnect() {
      setConnected(false);
    }
    function onMessageCreated(msg: ChatMessage) {
      if (msg.transactionId.toLowerCase() !== transactionId?.toLowerCase()) return;
      qc.setQueryData<ChatHistoryPage>(chatHistoryKey(transactionId), (prev) => {
        if (!prev) return { items: [msg], nextCursor: null, hasMore: false };
        if (prev.items.some((m) => m.id === msg.id)) return prev;
        return { ...prev, items: [msg, ...prev.items] };
      });
    }
    function onTyping(p: { transactionId: string; userId: string; isTyping: boolean }) {
      if (p.transactionId.toLowerCase() !== transactionId?.toLowerCase()) return;
      setTypingUserIds((prev) => {
        const next = new Set(prev);
        if (p.isTyping) next.add(p.userId);
        else next.delete(p.userId);
        return next;
      });
      // Auto-clear typing state if no follow-up arrives within 4s.
      if (p.isTyping) {
        window.clearTimeout(typingTimers.current[p.userId]);
        typingTimers.current[p.userId] = window.setTimeout(() => {
          setTypingUserIds((prev) => {
            const next = new Set(prev);
            next.delete(p.userId);
            return next;
          });
        }, 4_000);
      }
    }
    // Server fans out the updated cursor whenever any party calls
    // mark-read. Merge it into the receipts cache so own messages flip
    // to blue-tick the moment the counter-party reads.
    function onReadReceipt(p: ReadReceipt & { transactionId: string }) {
      if (p.transactionId.toLowerCase() !== transactionId?.toLowerCase()) return;
      qc.setQueryData<ThreadReceipts>(chatReceiptsKey(transactionId), (prev) => {
        const incoming: ReadReceipt = {
          threadId: p.threadId,
          userId: p.userId,
          lastReadMessageId: p.lastReadMessageId,
          lastReadAt: p.lastReadAt,
        };
        if (!prev) return { receipts: [incoming] };
        const others = prev.receipts.filter((r) => r.userId !== incoming.userId);
        return { receipts: [...others, incoming] };
      });
    }

    socket.on('connect', onConnect);
    socket.on('connect_error', onConnectError);
    socket.on('disconnect', onDisconnect);
    socket.on(WS_EVENTS.MESSAGE_CREATED, onMessageCreated);
    socket.on(WS_EVENTS.TYPING_UPDATED, onTyping);
    socket.on(WS_EVENTS.READ_RECEIPT, onReadReceipt);

    if (socket.connected) onConnect();

    return () => {
      socket.emit(WS_EVENTS.LEAVE, { transactionId });
      socket.off('connect', onConnect);
      socket.off('connect_error', onConnectError);
      socket.off('disconnect', onDisconnect);
      socket.off(WS_EVENTS.MESSAGE_CREATED, onMessageCreated);
      socket.off(WS_EVENTS.TYPING_UPDATED, onTyping);
      socket.off(WS_EVENTS.READ_RECEIPT, onReadReceipt);
    };
  }, [transactionId, qc]);

  const sendTyping = useMemo(
    () => (isTyping: boolean) => {
      if (!transactionId) return;
      const socket = getSocket();
      if (!socket.connected) return;
      socket.emit(WS_EVENTS.TYPING, { transactionId, isTyping });
    },
    [transactionId],
  );

  return { connected, typingUserIds, sendTyping };
}
